import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { X, BarChart3, TrendingUp, RefreshCw, Users, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AnalyticsPanel = ({ isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff/analytics/overview`);
      setData(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchAnalytics();
  }, [isOpen, fetchAnalytics]);

  const rankColors = {
    "SENIOR ADMIN": "#22d3ee",
    "ADMIN": "#a78bfa",
    "SENIOR MOD": "#f59e0b",
    "MOD": "#34d399",
    "JRMOD": "#60a5fa",
  };

  const maxRankCount = useMemo(
    () => data ? Math.max(...Object.values(data.rank_distribution || {}), 1) : 1,
    [data]
  );
  const sortedRanks = useMemo(
    () => Object.entries(data?.rank_distribution || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="glass-card w-full max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="analytics-panel"
      >
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-none bg-violet-500/20 border border-violet-500/30">
                <BarChart3 className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-white">Dashboard Analytics</h2>
                <p className="text-zinc-400 text-sm">Staff, quota, and feedback insights</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchAnalytics} className="p-2 hover:bg-zinc-800 rounded-none" data-testid="refresh-analytics">
                <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-none">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] scrollbar-thin space-y-6">
          {loading && !data ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading analytics...</p>
            </div>
          ) : data ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-card rounded-none p-4 border border-cyan-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-cyan-400" />
                    <p className="text-xs text-zinc-500">Total Staff</p>
                  </div>
                  <p className="text-3xl font-bold font-heading text-white">{data.total_staff}</p>
                </div>
                <div className="glass-card rounded-none p-4 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <p className="text-xs text-zinc-500">Passed</p>
                  </div>
                  <p className="text-3xl font-bold font-heading text-emerald-400">{data.activity_breakdown?.PASSED || 0}</p>
                </div>
                <div className="glass-card rounded-none p-4 border border-red-500/20">
                  <p className="text-xs text-zinc-500 mb-2">Failed</p>
                  <p className="text-3xl font-bold font-heading text-red-400">{data.activity_breakdown?.FAILED || 0}</p>
                </div>
                <div className="glass-card rounded-none p-4 border border-violet-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-violet-400" />
                    <p className="text-xs text-zinc-500">Feedback Reports</p>
                  </div>
                  <p className="text-3xl font-bold font-heading text-violet-400">{data.total_feedback}</p>
                </div>
              </div>

              {/* Rank Distribution */}
              <div className="glass-card rounded-none p-5 border border-zinc-800">
                <h3 className="font-heading font-bold text-white mb-4">Rank Distribution</h3>
                <div className="space-y-3">
                  {sortedRanks.map(([rank, count]) => (
                    <div key={rank} className="flex items-center gap-3">
                      <span className="text-sm text-zinc-400 w-32 text-right">{rank}</span>
                      <div className="flex-1 h-8 bg-zinc-800 rounded-none overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(count / maxRankCount) * 100}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className="h-full rounded-none flex items-center px-3"
                          style={{ backgroundColor: rankColors[rank] || "#64748b", minWidth: count > 0 ? "40px" : "0" }}
                        >
                          <span className="text-xs font-bold text-black">{count}</span>
                        </motion.div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(data.rank_distribution || {}).length === 0 && (
                    <p className="text-zinc-600 text-sm text-center py-4">No staff data to display</p>
                  )}
                </div>
              </div>

              {/* Quota Trend */}
              {data.quota_trend?.length > 0 && (
                <div className="glass-card rounded-none p-5 border border-zinc-800">
                  <h3 className="font-heading font-bold text-white mb-4">Quota Completion Trend</h3>
                  <div className="space-y-3">
                    {data.quota_trend.slice(0, 8).map((week) => (
                      <div key={week.week_start} className="flex items-center gap-3">
                        <span className="text-xs text-zinc-500 w-24 text-right font-mono">{week.week_start}</span>
                        <div className="flex-1 h-6 bg-zinc-800 rounded overflow-hidden flex">
                          <div
                            className="h-full bg-emerald-500/70 flex items-center justify-center"
                            style={{ width: `${week.pass_rate}%`, minWidth: week.passed > 0 ? "20px" : "0" }}
                          >
                            {week.pass_rate > 15 && <span className="text-[10px] font-bold text-white">{week.passed}</span>}
                          </div>
                          <div
                            className="h-full bg-red-500/70 flex items-center justify-center"
                            style={{ width: `${week.total > 0 ? (week.failed / week.total) * 100 : 0}%`, minWidth: week.failed > 0 ? "20px" : "0" }}
                          >
                            {week.failed > 0 && <span className="text-[10px] font-bold text-white">{week.failed}</span>}
                          </div>
                        </div>
                        <span className="text-xs text-emerald-400 w-12 text-right font-bold">{week.pass_rate}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-zinc-600">
                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500/70" /> Passed</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500/70" /> Failed</div>
                  </div>
                </div>
              )}

              {/* Feedback by Type */}
              {Object.keys(data.feedback_by_type || {}).length > 0 && (
                <div className="glass-card rounded-none p-5 border border-zinc-800">
                  <h3 className="font-heading font-bold text-white mb-4">Feedback by Type</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(data.feedback_by_type).map(([type, count]) => (
                      <div key={type} className={`rounded-none p-3 border ${
                        type === "Positive Staff Feedback" ? "border-emerald-500/20 bg-emerald-500/5" : "border-zinc-700 bg-zinc-800/50"
                      }`}>
                        <p className="text-xs text-zinc-500 mb-1">{type}</p>
                        <p className={`text-2xl font-bold font-heading ${
                          type === "Positive Staff Feedback" ? "text-emerald-400" : "text-white"
                        }`}>{count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-zinc-500 text-center py-12">Failed to load analytics</p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AnalyticsPanel;
